/**
 * ReferralDashboard
 *
 * Displays six aggregate referral metrics as stat cards with tooltips.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getRequest } from '../../utils/httpRequest';
import { API_ENDPOINTS } from './constants';
import { colors, statsCardStyles } from '../AgenticPaymentTransactionHistory/styles';

const STAT_CARDS = [
  {
    key: 'total_referrals',
    label: 'Total Referrals',
    gradient: 'linear-gradient(135deg, #2e3b61 0%, #3d4d7a 100%)',
    tooltip: 'Total number of users who signed up using a referral link or code.',
  },
  {
    key: 'pending_referrals',
    label: 'Pending',
    gradient: 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
    tooltip: 'Referrals that have been created but are not yet eligible for a reward, the referee has not completed the required transaction yet.',
  },

  {
    key: 'awaiting_payment',
    label: 'Awaiting Payment',
    gradient: 'linear-gradient(135deg, #e58734 0%, #d77a2a 100%)',
    tooltip: 'Referrals that are approved and queued for payout, waiting for an admin to trigger the Pay action.',
  },
  {
    key: 'rewards_paid',
    label: 'Rewards Paid',
    gradient: 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
    tooltip: 'Total number of referrals that have been successfully paid out.',
  },
  {
    key: 'total_rewards_distributed_usd',
    label: 'Total Distributed (USD)',
    gradient: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
    tooltip: 'Cumulative USD value of all rewards that have been paid out to referrers.',
    align: 'right',
  },
];

// ── Tooltip wrapper ───────────────────────────────────────────────────────────

// align: 'center' (default) | 'right'
const Tooltip = ({ text, children, align = 'center' }) => {
  const [visible, setVisible] = useState(false);

  const boxPos   = align === 'right'
    ? { right: 0, left: 'auto', transform: 'none' }
    : { left: '50%', transform: 'translateX(-50%)' };

  const arrowPos = align === 'right'
    ? { right: '6px', left: 'auto', transform: 'none' }
    : { left: '50%', transform: 'translateX(-50%)' };

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          ...boxPos,
          background: 'rgba(0,0,0,0.85)',
          color: '#fff',
          fontSize: '12px',
          lineHeight: '1.5',
          padding: '8px 12px',
          borderRadius: '6px',
          whiteSpace: 'normal',
          width: '220px',
          textAlign: 'center',
          zIndex: 100,
          pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        }}>
          {text}
          <div style={{
            position: 'absolute',
            top: '100%',
            ...arrowPos,
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid rgba(0,0,0,0.85)',
          }} />
        </div>
      )}
    </div>
  );
};

// ── Info icon ─────────────────────────────────────────────────────────────────

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ display: 'block' }}>
    <circle cx="7" cy="7" r="6.5" stroke="rgba(255,255,255,0.6)" strokeWidth="1" fill="none" />
    <text x="7" y="11" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.8)" fontWeight="700">i</text>
  </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────

const ReferralDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRequest(API_ENDPOINTS.DASHBOARD);
      if (!data.success) throw new Error(data.error || 'Failed to load dashboard');
      setMetrics(data.data);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) {
    return (
      <div style={statsCardStyles.container}>
        {STAT_CARDS.map((card) => (
          <div key={card.key} style={{ ...statsCardStyles.card, background: card.gradient, opacity: 0.5 }}>
            <span style={statsCardStyles.value}>-</span>
            <span style={statsCardStyles.label}>{card.label}</span>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px', background: '#f8d7da', color: '#721c24', borderRadius: '6px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{error}</span>
        <button
          type="button"
          onClick={fetchDashboard}
          style={{ padding: '6px 14px', background: '#721c24', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={statsCardStyles.container} data-testid="referral-dashboard">
      {STAT_CARDS.map((card) => {
        const value   = metrics ? metrics[card.key] : 0;
        const display = card.key === 'total_rewards_distributed_usd'
          ? `$${Number(value).toFixed(2)}`
          : value;

        return (
          <div key={card.key} style={{ ...statsCardStyles.card, background: card.gradient, position: 'relative' }}>
            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
              <Tooltip text={card.tooltip} align={card.align || 'center'}>
                <InfoIcon />
              </Tooltip>
            </div>

            <span style={statsCardStyles.value}>{display}</span>
            <span style={statsCardStyles.label}>{card.label}</span>
          </div>
        );
      })}
    </div>
  );
};

export default ReferralDashboard;
